import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Button,
  Indicators,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from 'ui'
import CreatableSelect from 'react-select/creatable'
import { useDebounce } from 'utils'
import { useCurrentUser } from '../../../queries/useCurrentUser'
import { useSuggestedKeywordsForEvent } from '../../../queries/useSuggestedKeywordsForEvent'
import { useKeywordSearch } from '../../../queries/useKeywordSearch'
import { useCreateKeywordMutation } from '../../../queries/useCreateKeywordMutation'
import { useUpdateEventMutation } from '../../../queries/useUpdateEventMutation'
import { useEvent } from '../../../queries/useEvent'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
export interface CreateEventStepTheeFormProps {
  eventId: string
}
import { toast } from 'react-hot-toast'

// Validation schema
const schema = yup.object({
  keywords: yup.array().of(yup.string().required()).optional(),
})
export type EventStepThree = yup.InferType<typeof schema>

export const CreateEventStepThreeForm: React.FC<CreateEventStepTheeFormProps> = ({
  eventId,
}) => {
  const { data: user, isLoading } = useCurrentUser()
  const isEducator = user?.userRole?.toLowerCase() === 'educator'
  const navigate = useNavigate()
  const form = useForm<EventStepThree>({
    defaultValues: {
      keywords: [],
    } as EventStepThree,
    reValidateMode: 'onChange',
    resolver: yupResolver(schema as any) as any,
  })

  // Fetch the actual event data to get existing keywords
  const { data: event } = useEvent(eventId)

  const {
    data: suggestedKeywords,
    isLoading: loadingKeywords,
  } = useSuggestedKeywordsForEvent(eventId)

  // Track the current input value for search
  const [keywordInput, setKeywordInput] = useState('')
  const debouncedKeywordInput = useDebounce(keywordInput, 300)

  // Search for keywords based on debounced input
  const { data: searchedKeywords } = useKeywordSearch(
    debouncedKeywordInput,
    debouncedKeywordInput.length >= 2
  )

  useEffect(() => {
    // Priority: Use event's actual keywords if they exist, otherwise use suggested keywords
    if (event?.keywords && event.keywords.length > 0) {
      console.log('[Keywords] Loading existing event keywords:', event.keywords)
      form.setValue('keywords', event.keywords)
    } else if (suggestedKeywords && suggestedKeywords.length > 0) {
      console.log(
        '[Keywords] Loading suggested keywords:',
        suggestedKeywords.map((k) => k.keyword)
      )
      form.setValue(
        'keywords',
        suggestedKeywords
          .map((k) => k.keyword)
          .filter((_, i) => i < 4) as string[]
      )
    }
  }, [event?.keywords, suggestedKeywords, form])
  const { mutateAsync: createKeyword } = useCreateKeywordMutation()
  const [userAddedKeywords, setUserAddedKeywords] = useState([] as string[])
  const addKeyword = async (keyword: string) => {
    setUserAddedKeywords([...(userAddedKeywords ?? []), keyword])
    console.log('[Keywords] Calling POST /keyword API:', { keyword })
    const result = await createKeyword({ keyword })
    console.log('[Keywords] POST /keyword response:', result)
  }
  const userKeywords = user?.keywords ?? []
  const currentKeywords = form.watch('keywords') ?? []
  const usableKeywords = Array.from(
    new Set([
      ...(suggestedKeywords?.map((k) => k.keyword) ?? []),
      ...(searchedKeywords?.map((k) => k.keyword) ?? []),
      ...userKeywords,
      ...userAddedKeywords,
      ...currentKeywords,
    ])
  )

  const createEventMutation = useUpdateEventMutation()
  const handleSubmitForm = (
    redirect: string = '/app/events/{id}/four'
  ) => async (values: EventStepThree) => {
    try {
      // Always persist to the API first to get an ID
      console.log('[Keywords] Submitting event update:', {
        eventId,
        keywords: values.keywords,
        keywordCount: values.keywords?.length || 0,
      })
      const response = await createEventMutation.mutateAsync({
        ...values,
        id: eventId,
        keywords: values.keywords,
      })
      console.log('[Keywords] Event update response received:', {
        eventId: response.id,
        keywords: response.keywords,
        keywordCount: response.keywords?.length || 0,
      })

      // If redirecting to dashboard, show success toast
      if (redirect === '/app/dashboard') {
        toast.success('Event draft saved successfully')
      } else {
        toast.success('Event details saved successfully')
      }

      // Handle dynamic redirect with event ID
      const targetRedirect = redirect.includes('{id}')
        ? redirect.replace('{id}', response.id)
        : redirect

      navigate(targetRedirect)
    } catch (error) {
      console.error('Error creating event:', error)
      toast.error('There was an error creating the event')
    }
  }
  const onSaveDraft = async () => {
    await form.handleSubmit(handleSubmitForm('/app/dashboard'))() // Call the regular submit handler
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmitForm())}
        className={clsx(isLoading && 'opacity-50')}
      >
        <div className="flex flex-col">
          <FormLabel>Suggested Keywords</FormLabel>
          <p className="font-body text-sm mt-1">
            Real-world keywords help us link your profile to the things that{' '}
            {isEducator
              ? 'professionals do every day'
              : 'educators focus on every day'}
            . Based on what you've told us so far, adding the following keywords
            to your profile will expose you to{' '}
            {isEducator ? 'professionals' : 'educators'} with similar interests.{' '}
            <span className="font-bold">
              You can always uncheck keywords that don't apply and add others.
            </span>
          </p>
          <p className="font-body text-sm mt-3 text-body/70">
            Note: For matching to work effectively,{' '}
            <span className="font-bold">please choose 4-6 keywords.</span>
          </p>
          <div className="relative py-3">
            <FormField
              control={form.control}
              name="keywords"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="hidden">Keywords</FormLabel>
                  <FormControl>
                    <CreatableSelect
                      isDisabled={form.formState.isSubmitting}
                      isMulti
                      isClearable={false}
                      placeholder="Select or create keywords"
                      options={usableKeywords.map((keyword) => ({
                        value: keyword,
                        label: keyword,
                      }))}
                      onInputChange={(inputValue) => {
                        setKeywordInput(inputValue)
                      }}
                      value={
                        usableKeywords
                          ?.filter((keyword) => field.value?.includes(keyword))
                          .map((keyword) => ({
                            value: keyword,
                            label: keyword,
                          })) || []
                      }
                      onChange={(selectedOptions) => {
                        const newKeywords = selectedOptions.map(
                          (option) => option.value
                        )
                        console.log('[Keywords] Selection changed:', {
                          previous: field.value,
                          new: newKeywords,
                          added: newKeywords.filter(
                            (k) => !field.value?.includes(k)
                          ),
                          removed:
                            field.value?.filter(
                              (k) => !newKeywords.includes(k)
                            ) || [],
                        })
                        field.onChange(newKeywords)
                      }}
                      onCreateOption={(inputValue) => {
                        console.log('[Keywords] Creating new keyword:', {
                          keyword: inputValue,
                          currentKeywords: field.value,
                          eventId,
                        })
                        field.onChange([...(field.value || []), inputValue])
                        addKeyword(inputValue)
                      }}
                      classNames={{
                        control: () =>
                          'flex w-full rounded-md border border-input bg-background py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <p className="text-xs font-body text-body/70 md:mr-28 mt-1">
              When you start typing, we'll suggest keywords that are already in
              our system. Selecting existing keywords will help with accuracy,
              and, you won't have to type as much nor worry about spelling
              errors!
            </p>
            {loadingKeywords ? <Indicators.AbsoluteOverlay /> : null}
          </div>
        </div>

        <div className="flex-col flex mt-12 w-full">
          <Button
            type="submit"
            disabled={form.formState.isSubmitting}
            variant="default"
            className="mx-auto uppercase"
          >
            {form.formState.isSubmitting ? (
              <Indicators.Spinner
                size="lg"
                className="mt-auto mr-2 mb-auto ml-auto"
              />
            ) : null}
            <span>Next: Publish Your Project &amp; See Your Matches &gt;</span>
          </Button>
          <Button
            type="button"
            variant="link"
            className="text-actionable underline text-sm"
            onClick={onSaveDraft}
          >
            <span>Save Draft and Finish Later</span>
          </Button>
        </div>
      </form>
    </Form>
  )
}
